import React, { useState } from 'react';
import { useMembers, useUpdateMemberStatus } from '../../../hooks/useMembers';
import { useToast } from '../../../hooks/useToast';
import MembersTable from '../../../components/tables/MembersTable';
import Loading from '../../../components/common/Loading';
import ErrorMessage from '../../../components/common/ErrorMessage';
import Pagination from '../../../components/common/Pagination';
import { Search, Filter } from 'lucide-react';

const MembersList = () => {
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ search: '', status: '' });

  const { data, isLoading, error, refetch } = useMembers({ ...filters, page });
  const updateStatus = useUpdateMemberStatus();
  const toast = useToast();

  const handleFilterChange = (field, value) => {
    setPage(1);
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const clearFilters = () => {
    setPage(1);
    setFilters({ search: '', status: '' });
  };

  const handleUpdateStatus = async (id, status) => {
    const labels = { active: 'aprovado', inactive: 'inativado/rejeitado' };
    try {
      await updateStatus.mutateAsync({ id, status });
      toast.success(`Associado ${labels[status] || 'atualizado'} com sucesso!`);
    } catch (err) {
      toast.error('Erro ao atualizar status: ' + err.message);
    }
  };

  if (isLoading) return <Loading text="Carregando associados..." />;
  if (error)    return <ErrorMessage message={error.message} onRetry={refetch} />;

  const members = data?.data || [];
  const meta = data?.meta || {};

  const totalPending  = meta.total_pending ?? 0;
  const totalActive   = meta.total_active ?? 0;
  const totalInactive = meta.total_inactive ?? 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Associados</h1>
        <p className="text-gray-600 mt-1">Gerencie os associados e aprovações de cadastro</p>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center mb-4">
          <Filter className="h-5 w-5 text-gray-600 mr-2" />
          <h2 className="text-lg font-semibold text-gray-900">Filtros</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="label">Buscar</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Nome, email ou CPF..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="input pl-10"
              />
            </div>
          </div>

          <div>
            <label className="label">Status</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="input"
            >
              <option value="">Todos</option>
              <option value="pending">Pendente</option>
              <option value="active">Ativo</option>
              <option value="inactive">Inativo</option>
            </select>
          </div>
        </div>

        <div className="mt-4 flex justify-end">
          <button
            onClick={clearFilters}
            className="text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            Limpar filtros
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="text-sm text-gray-600 mb-1">Pendentes</div>
          <div className="text-3xl font-bold text-yellow-600">{totalPending}</div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="text-sm text-gray-600 mb-1">Ativos</div>
          <div className="text-3xl font-bold text-green-600">{totalActive}</div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="text-sm text-gray-600 mb-1">Inativos</div>
          <div className="text-3xl font-bold text-red-600">{totalInactive}</div>
        </div>
      </div>

      {/* Tabela */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <MembersTable members={members} onUpdateStatus={handleUpdateStatus} />
        <Pagination
          page={data?.page || 1}
          pageSize={data?.pageSize || 15}
          total={data?.total || 0}
          onPageChange={setPage}
        />
      </div>
    </div>
  );
};

export default MembersList;
